import type { ReactNode } from 'react';
import { Fragment2 } from '@/common/fragment/fragment2';
import scrollIntoViewWithOffset from '@/common/utils/scrollIntoViewWithOffset';
import RaviGlobalContextsProvider from '../../../../.plasmic/plasmic/ravi_r_r/PlasmicGlobalContextsProvider';
import ProfileGlobalContextsProvider from '../../../../.plasmic/plasmic/paziresh_24_profile/PlasmicGlobalContextsProvider';
import { PlasmicProfileHead } from '.plasmic/plasmic/paziresh_24_profile/PlasmicProfileHead';
import PlasmicRateAndCommentCount from '.plasmic/plasmic/ravi_r_r/PlasmicRateAndCommentCount';
import { RatePillSkeleton } from '@/modules/profile/components/ProfileSectionSkeleton';

export type ProfileHeadRate = {
  /** formatted average, e.g. "4.7" — or false when rates must not be shown */
  rate: string | false | undefined;
  count: number | undefined;
  hideRates: boolean | undefined;
};

type Props = {
  pageViewCount?: string | number;
  serviceList: unknown[];
  displayName?: string;
  title?: string;
  subTitle?: string;
  imageUrl?: string;
  slug?: string;
  /** Hamdast "head" widgets (profile page only). */
  widgets?: ReactNode;
  /** null → no rate box; 'pending' → rate placeholder; otherwise the rate line. */
  rate: ProfileHeadRate | 'pending' | null;
  /** Extra args for the rate component (the page passes its profile data). */
  rateArgs?: Record<string, unknown>;
  /** Reserve the 29px rate line height while it may still change (client shell). */
  reserveRateHeight?: boolean;
};

/**
 * The profile header — the single source for both the profile page and the search → profile
 * navigation preview, so the two can never drift apart.
 */
export const ProfileHead = ({
  pageViewCount,
  serviceList,
  displayName,
  title,
  subTitle,
  imageUrl,
  slug,
  widgets,
  rate,
  rateArgs,
  reserveRateHeight,
}: Props) => (
  <ProfileGlobalContextsProvider>
    <Fragment2
      Component={PlasmicProfileHead}
      name="ProfileHead"
      args={{
        pageViewCount,
        serviceList,
        displayName,
        title,
        subTitle,
        imageUrl,
        slug,
        children: (
          <div className="flex flex-col w-full gap-2">
            {widgets}
            {rate !== null && (
              <RaviGlobalContextsProvider>
                <div
                  className={`self-center cursor-pointer${reserveRateHeight ? ' min-h-[29px]' : ''}`}
                  role="button"
                  tabIndex={0}
                  aria-label="مشاهده نظرات و امتیاز پزشک"
                  onClick={() => scrollIntoViewWithOffset('#reviews', 90)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      scrollIntoViewWithOffset('#reviews', 90);
                    }
                  }}
                >
                  {rate === 'pending' ? (
                    <RatePillSkeleton />
                  ) : (
                    <Fragment2
                      name="RateAndCommentCount2"
                      Component={PlasmicRateAndCommentCount}
                      args={{ ...rateArgs, rateCount: rate.count, rate: rate.rate, hideRates: rate.hideRates }}
                    />
                  )}
                </div>
              </RaviGlobalContextsProvider>
            )}
          </div>
        ),
      }}
    />
  </ProfileGlobalContextsProvider>
);
